/*
 * ModifierUtilisateurs.js
 * 
 * Ce composant permet de modifier les informations d'un utilisateur, y compris sa position géographique
 * en utilisant Google Maps. L'utilisateur peut cliquer sur la carte pour sélectionner sa position,
 * et les coordonnées (latitude/longitude) sont automatiquement mises à jour dans le formulaire.
 * 
 * IMPORTANT: Pour que la carte fonctionne correctement, vous devez obtenir une clé API Google Maps
 * et la configurer dans la fonction loadGoogleMapsScript (recherchez 'key=' dans ce fichier).
 * Documentation: https://developers.google.com/maps/documentation/javascript/get-api-key
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, MenuItem, Select, FormControl, Button, TextField, CircularProgress, Alert, IconButton } from '@mui/material';
import Navbar from '../Layouts/Navbar';
import { Person } from '@mui/icons-material';
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';


export default function ModifierUtilisateurs() {
    const [formData, setFormData] = useState({
        site: "",
        matricule: "",
        zone: "",
        telephone: "",
        adresse: "",
        role: "",
        email: "",
        motDePasse: "",
        confirmation: "",
        adhesion: false,
        date_adhesion: "",
        latitude: "",
        longitude: "",
        Uid: "",
      });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    
    // State for sites, roles, and pickup zones
    const [sites, setSites] = useState([]);
    const [roles, setRoles] = useState([]);
    const [pickupZones, setPickupZones] = useState([]);
    
    // Loading states
    const [sitesLoading, setSitesLoading] = useState(true);
    const [rolesLoading, setRolesLoading] = useState(true);
    const [pickupZonesLoading, setPickupZonesLoading] = useState(true);
    
    // Error states
    const [sitesError, setSitesError] = useState(false);
    const [rolesError, setRolesError] = useState(false);
    const [pickupZonesError, setPickupZonesError] = useState(false);
    
    // Fetch sites from API
    const fetchSites = async (showErrorMessage = true) => {
      setSitesLoading(true);
      setSitesError(false);
      try {
        const response = await api.get('/sites');
        setSites(response.data || []);
      } catch (err) {
        console.error('Error fetching sites:', err);
        setSitesError(true);
        if (showErrorMessage) {
          setError(prev => prev || 'Erreur lors du chargement des sites');
        }
      } finally {
        setSitesLoading(false);
      }
    };
    
    // Retry fetching sites
    const retryFetchSites = () => {
      fetchSites(true);
    };
    
    // Fetch roles from API
    const fetchRoles = async (showErrorMessage = true) => {
      setRolesLoading(true);
      setRolesError(false);
      try {
        const response = await api.get('/roles');
        setRoles(response.data || []);
      } catch (err) {
        console.error('Error fetching roles:', err);
        setRolesError(true);
        if (showErrorMessage) {
          setError(prev => prev || 'Erreur lors du chargement des rôles');
        }
      } finally {
        setRolesLoading(false);
      }
    };
    
    // Retry fetching roles
    const retryFetchRoles = () => {
      fetchRoles(true);
    };
    
    // Fetch pickup zones from API
    const fetchPickupZones = async (showErrorMessage = true) => {
      setPickupZonesLoading(true);
      setPickupZonesError(false);
      try {
        const response = await api.get('/pickups'); // Assuming this is the correct endpoint
        setPickupZones(response.data || []);
      } catch (err) {
        console.error('Error fetching pickup zones:', err);
        setPickupZonesError(true);
        if (showErrorMessage) {
          setError(prev => prev || 'Erreur lors du chargement des zones');
        }
      } finally {
        setPickupZonesLoading(false);
      }
    };
    
    // Retry fetching pickup zones
    const retryFetchPickupZones = () => {
      fetchPickupZones(true);
    };
    
    // Récupérer l'ID utilisateur depuis l'URL et charger les données
    useEffect(() => {
      const fetchUserData = async () => {
        try {
          // Get userId from URL path params
          const userId = location.pathname.split('/').pop();
          
          if (!userId) {
            setError('ID utilisateur non spécifié');
            setLoading(false);
            return;
          }
          
          // Fetch user data from API
          const response = await api.get(`/users/${userId}`);
          const userData = response.data;
          
          // Mettre à jour le formulaire avec les données de l'utilisateur
          setFormData({
            site: userData.ID_Site || "",
            matricule: userData.matricule || "",
            zone: userData.id_pickup || "",
            telephone: userData.telephone || "",
            adresse: userData.adresse || "",
            role: userData.ID_Role || "",
            email: userData.email || "",
            motDePasse: "",
            confirmation: "",
            adhesion: userData.adhesion || false,
            date_adhesion: userData.date_adhesion || "",
            latitude: userData.latitude || "",
            longitude: userData.longitude || "",
            Uid: userData.Uid || "",
          });
          
          // Fetch dropdown data
          await Promise.all([
            fetchSites(false),
            fetchRoles(false),
            fetchPickupZones(false)
          ]);
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching user details:', err);
          setError(err.response?.data?.message || 'Erreur lors du chargement des données utilisateur');
          setLoading(false);
        }
      };
      
      fetchUserData();
    }, [location]);
    
      const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
        
        // Si les champs de latitude ou longitude sont modifiés manuellement
        if ((name === 'latitude' || name === 'longitude') && value === '') {
          // Si l'un des champs est vidé, on supprime le marqueur
          if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
          }
        }
      };
      
      // Fonction pour effacer les coordonnées et supprimer le marqueur
      const clearCoordinates = (field) => {
        // Mettre à jour le formulaire
        setFormData(prev => ({ ...prev, [field]: "" }));
        
        // Si les deux champs sont vides, supprimer le marqueur
        if ((field === 'latitude' && !formData.longitude) || 
            (field === 'longitude' && !formData.latitude)) {
          if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
          }
        }
      };
    
      const inputStyle = {
        '& .MuiOutlinedInput-root': {
          height: 35,
          '&:hover fieldset': { borderColor: '#000' },
          '&.Mui-focused fieldset': { borderColor: 'red' }
        }
      };
  // Fonction pour gérer la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation côté client
    let validationErrors = {};
    
    // Vérifier que les mots de passe correspondent s'ils sont renseignés
    if (formData.motDePasse && formData.motDePasse !== formData.confirmation) {
      validationErrors.confirmation = 'Les mots de passe ne correspondent pas';
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    try {
      const userId = location.pathname.split('/').pop();
      
      // Préparer les données à envoyer
      const dataToSubmit = {
        ID_Site: formData.site,
        matricule: formData.matricule,
        id_pickup: formData.zone,
        telephone: formData.telephone,
        adresse: formData.adresse,
        ID_Role: formData.role,
        email: formData.email,
        adhesion: formData.adhesion,
        date_adhesion: formData.date_adhesion,
        latitude: formData.latitude,
        longitude: formData.longitude,
        Uid: formData.Uid
      };
      
      // Ajouter le mot de passe seulement s'il est renseigné
      if (formData.motDePasse) {
        dataToSubmit.password = formData.motDePasse;
      }
      
      // Réinitialiser les erreurs
      setError(null);
      
      // Envoyer les données à l'API
      await api.put(`/users/${userId}`, dataToSubmit);
      
      // Rediriger vers la page de détails
      navigate(`/utilisateurs-details/${userId}`);
    } catch (err) {
      console.error('Error updating user:', err);
      
      // Gérer les erreurs de validation du serveur
      if (err.response && err.response.data) {
        if (err.response.data.errors) {
          // Traitement des erreurs de validation spécifiques
          const errorMessage = Object.values(err.response.data.errors)
            .flat()
            .join('\n');
          setError(errorMessage || 'Erreur de validation');
        } else if (err.response.data.message) {
          // Message d'erreur général
          setError(err.response.data.message);
        } else {
          setError('Erreur lors de la mise à jour de l\'utilisateur');
        }
      } else {
        setError('Erreur de connexion au serveur');
      }
    }
  };
  
  // Fonction pour annuler et retourner à la page précédente
  const handleCancel = () => {
    navigate(-1);
  };
  
  // Fonction pour initialiser la carte Google Maps
  useEffect(() => {
    if (!loading && !error) {
      // Fonction pour charger le script Google Maps
      const loadGoogleMapsScript = () => {
        // Vérifier si le script est déjà chargé
        if (window.google && window.google.maps) {
          initializeMap();
          return;
        }
        
        // Créer le script
         const script = document.createElement('script');
         // NOTE: Vous devez remplacer la valeur vide de 'key=' par votre clé API Google Maps
         // Pour obtenir une clé API: https://developers.google.com/maps/documentation/javascript/get-api-key
         script.src = `https://maps.googleapis.com/maps/api/js?key=&callback=initMap`;
         script.async = true;
         script.defer = true;
        
        // Définir la fonction de callback globale
        window.initMap = initializeMap;
        
        // Ajouter le script au document
         document.head.appendChild(script);
         
         // Gérer les erreurs de chargement
         script.onerror = () => {
           console.error('Erreur lors du chargement de Google Maps API');
           // Afficher un message d'erreur à l'utilisateur
           const mapElement = document.getElementById('map');
           if (mapElement) {
             mapElement.innerHTML = `
               <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; background-color: #f5f5f5; padding: 20px; text-align: center;">
                 <p style="color: red;">Impossible de charger Google Maps</p>
                 <p>Veuillez vérifier votre connexion internet ou la clé API Google Maps.</p>
                 <p>Vous pouvez toujours saisir manuellement vos coordonnées dans les champs ci-dessus.</p>
               </div>
             `;
           }
         };
      };
      
      // Fonction pour initialiser la carte
      const initializeMap = () => {
        // Coordonnées par défaut (Bangkok, Thaïlande)
        const defaultPosition = { lat: 13.7249, lng: 100.5465 };
        
        // Créer la carte
        const map = new window.google.maps.Map(document.getElementById('map'), {
          center: formData.latitude && formData.longitude 
            ? { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) } 
            : defaultPosition,
          zoom: 15,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });
        
        mapRef.current = map;
        
        // Créer un marqueur si des coordonnées existent déjà
        if (formData.latitude && formData.longitude) {
          const marker = new window.google.maps.Marker({
            position: { lat: parseFloat(formData.latitude), lng: parseFloat(formData.longitude) },
            map: map,
            draggable: true,
            animation: window.google.maps.Animation.DROP,
            title: 'Votre position'
          });
          
          markerRef.current = marker;
          
          // Ajouter un événement pour mettre à jour les coordonnées lors du déplacement du marqueur
          marker.addListener('dragend', () => {
            const position = marker.getPosition();
            setFormData(prev => ({
              ...prev,
              latitude: position.lat().toFixed(6),
              longitude: position.lng().toFixed(6)
            }));
          });
        }
        
        // Ajouter un événement de clic sur la carte
        map.addListener('click', (event) => {
          const clickedPosition = event.latLng;
          
          // Mettre à jour les champs de latitude et longitude
          setFormData(prev => ({
            ...prev,
            latitude: clickedPosition.lat().toFixed(6),
            longitude: clickedPosition.lng().toFixed(6)
          }));
          
          // Ajouter ou déplacer le marqueur
          if (markerRef.current) {
            markerRef.current.setPosition(clickedPosition);
          } else {
            const marker = new window.google.maps.Marker({
              position: clickedPosition,
              map: map,
              draggable: true,
              animation: window.google.maps.Animation.DROP,
              title: 'Votre position'
            });
            
            markerRef.current = marker;
            
            // Ajouter un événement pour mettre à jour les coordonnées lors du déplacement du marqueur
            marker.addListener('dragend', () => {
              const position = marker.getPosition();
              setFormData(prev => ({
                ...prev,
                latitude: position.lat().toFixed(6),
                longitude: position.lng().toFixed(6)
              }));
            });
          }
        });
      };
      
      loadGoogleMapsScript();
    }
  }, [loading, error, formData.latitude, formData.longitude]);
  
  return (
    <>
      <Navbar />
      <Box p={3}>
        <Box display="flex" alignItems="center">
          <Person />
          <Typography variant="h6" ml={1}> MODIFIER MES INFORMATIONS </Typography>
        </Box>
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress color="error" />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Box display="flex" mt={1}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
        <Typography variant="subtitle2">Site</Typography>
        <Box display="flex" alignItems="center" width="100%">
          <FormControl fullWidth sx={inputStyle}>
            <Select 
              name="site" 
              value={formData.site} 
              onChange={handleChange}
              disabled={sitesLoading}
            >
              <MenuItem value="">Sélectionner un site</MenuItem>
              {sitesLoading ? (
                <MenuItem disabled>Chargement des sites...</MenuItem>
              ) : sitesError ? (
                <MenuItem disabled sx={{ color: 'error.main' }}>
                  Erreur de chargement
                </MenuItem>
              ) : (
                sites.map(site => (
                  <MenuItem key={site.ID_Site} value={site.ID_Site}>{site.name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <IconButton 
            onClick={retryFetchSites} 
            disabled={sitesLoading}
            sx={{ ml: 1, color: sitesError ? 'error.main' : 'inherit' }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2">Matricule</Typography>
        <TextField fullWidth type="text" name="matricule" value={formData.matricule} onChange={handleChange} variant="outlined" sx={inputStyle} />

        <Typography variant="subtitle2">Zone</Typography>
        <Box display="flex" alignItems="center" width="100%">
          <FormControl fullWidth sx={inputStyle}>
            <Select 
              name="zone" 
              value={formData.zone} 
              onChange={handleChange}
              disabled={pickupZonesLoading}
            >
              <MenuItem value="">Sélectionner une zone</MenuItem>
              {pickupZonesLoading ? (
                <MenuItem disabled>Chargement des zones...</MenuItem>
              ) : pickupZonesError ? (
                <MenuItem disabled sx={{ color: 'error.main' }}>
                  Erreur de chargement
                </MenuItem>
              ) : (
                pickupZones.map(zone => (
                  <MenuItem key={zone.id_pickup} value={zone.id_pickup}>{zone.name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <IconButton 
            onClick={retryFetchPickupZones} 
            disabled={pickupZonesLoading}
            sx={{ ml: 1, color: pickupZonesError ? 'error.main' : 'inherit' }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2">Téléphone</Typography>
        <TextField fullWidth type="text" name="telephone" value={formData.telephone} onChange={handleChange} variant="outlined" sx={inputStyle} />

        <Typography variant="subtitle2">Adresse</Typography>
        <TextField fullWidth type="text" name="adresse" value={formData.adresse} onChange={handleChange} variant="outlined" sx={inputStyle} />

        <Typography variant="subtitle2">Rôle</Typography>
        <Box display="flex" alignItems="center" width="100%">
          <FormControl fullWidth sx={inputStyle}>
            <Select 
              name="role" 
              value={formData.role} 
              onChange={handleChange}
              disabled={rolesLoading}
            >
              <MenuItem value="">Sélectionner un rôle</MenuItem>
              {rolesLoading ? (
                <MenuItem disabled>Chargement des rôles...</MenuItem>
              ) : rolesError ? (
                <MenuItem disabled sx={{ color: 'error.main' }}>
                  Erreur de chargement
                </MenuItem>
              ) : (
                roles.map(role => (
                  <MenuItem key={role.ID_Role} value={role.ID_Role}>{role.Role}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <IconButton 
            onClick={retryFetchRoles} 
            disabled={rolesLoading}
            sx={{ ml: 1, color: rolesError ? 'error.main' : 'inherit' }}
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        <Typography variant="subtitle2">Email</Typography>
        <TextField fullWidth type="email" name="email" value={formData.email} onChange={handleChange} variant="outlined" sx={inputStyle} />

        <Typography variant="subtitle2">Mot de passe</Typography>
        <TextField fullWidth type="password" name="motDePasse" value={formData.motDePasse} onChange={handleChange} variant="outlined" sx={inputStyle} />

        <Typography variant="subtitle2">Confirmation</Typography>
        <TextField fullWidth type="password" name="confirmation" value={formData.confirmation} onChange={handleChange} variant="outlined" sx={inputStyle} />

        <Typography variant="subtitle2">Adhésion</Typography>
        <FormControl fullWidth sx={inputStyle}>
          <Select
            name="adhesion"
            value={formData.adhesion === 1 ? true : formData.adhesion === 0 ? false : ""}
            onChange={handleChange}
          >
            <MenuItem value={true}>Oui</MenuItem>
            <MenuItem value={false}>Non</MenuItem>
            <MenuItem value="">Non spécifié</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle2">Date d'adhésion</Typography>
        <TextField 
          fullWidth 
          type="date" 
          name="date_adhesion" 
          value={formData.date_adhesion} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          InputLabelProps={{ shrink: true }}
        />

        <Typography variant="subtitle2">UID</Typography>
        <TextField 
          fullWidth 
          type="text" 
          name="Uid" 
          value={formData.Uid} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
        />

        <Typography variant="subtitle2">Latitude</Typography>
        <Box display="flex" alignItems="center" width="100%">
          <TextField 
            fullWidth 
            type="number" 
            name="latitude" 
            value={formData.latitude} 
            onChange={handleChange} 
            variant="outlined" 
            sx={inputStyle} 
            InputProps={{
              endAdornment: formData.latitude && (
                <IconButton 
                  size="small" 
                  onClick={() => clearCoordinates('latitude')}
                  sx={{ color: 'gray' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }}
          />
        </Box>

        <Typography variant="subtitle2">Longitude</Typography>
        <Box display="flex" alignItems="center" width="100%">
          <TextField 
            fullWidth 
            type="number" 
            name="longitude" 
            value={formData.longitude} 
            onChange={handleChange} 
            variant="outlined" 
            sx={inputStyle} 
            InputProps={{
              endAdornment: formData.longitude && (
                <IconButton 
                  size="small" 
                  onClick={() => clearCoordinates('longitude')}
                  sx={{ color: 'gray' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }}
          />
        </Box>

        <Typography variant="subtitle2">Merci de sélectionner les cordonnées GPS de votre domicile sur la carte, afin de les utiliser comme base lors de la planification votre transport. <strong>Cliquez sur la carte pour sélectionner votre position.</strong></Typography>
           <Box>
                <div 
                  id="map" 
                  style={{ 
                    width: '100%', 
                    height: '500px', 
                    borderRadius: '4px',
                    position: 'relative'
                  }}
                >
                  {/* Map will be loaded here */}
                </div>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: formData.latitude && formData.longitude ? 'green' : 'inherit' }}>
                    {formData.latitude && formData.longitude ? 
                      `Position sélectionnée: ${formData.latitude}, ${formData.longitude}` : 
                      'Aucune position sélectionnée. Cliquez sur la carte pour définir votre position.'}
                  </Typography>
                  {formData.latitude && formData.longitude && mapRef.current && (
                    <Button 
                      size="small" 
                      variant="text" 
                      sx={{ color: 'red', textTransform: 'none' }}
                      onClick={() => {
                        if (mapRef.current && formData.latitude && formData.longitude) {
                          const position = { 
                            lat: parseFloat(formData.latitude), 
                            lng: parseFloat(formData.longitude) 
                          };
                          mapRef.current.setCenter(position);
                          mapRef.current.setZoom(15);
                        }
                      }}
                    >
                      Centrer sur la position
                    </Button>
                  )}
                </Box>
                </Box>
        
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleCancel}
                sx={{ 
                  color: "red", 
                  borderColor: "red", 
                  textTransform: "none", 
                  "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } 
                }} 
                startIcon={<CloseIcon />}
              >
                Annuler
              </Button>
              <Button 
                variant="outlined" 
                type="submit"
                sx={{ 
                  color: "red", 
                  borderColor: "red", 
                  textTransform: "none", 
                  "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } 
                }} 
                startIcon={<CheckIcon />}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
